/**
 * The banners every logged-in device shows (part 36c, principle C11; `cockatrice-ui`: `Account.jsx`'s reset banner, and
 * the others built in its look, the user's choice 2026-10-24): a password reset waiting, with Stop (RQ4); the account's
 * deletion waiting, with Cancel and a backup first (DA2); any other change waiting its 72 h, with Cancel (PD4); a new
 * device's login not seen here (NO2); the terms asked again (TA1). None blocks reading, writing or export; the urgent
 * two are said once as they appear (an alert), the others are named regions.
 */
import { LEGAL_VERSIONS } from '../account/legal.ts';
import type { ChangeEntry } from '../account/wire.ts';
import { AcceptTerms } from './account-page.ts';
import { changeWords, dateWords, timeWords } from './account-sheet.ts';
import Banner from '../ui/Banner.ts';
import Button from '../ui/Button.ts';
import { component, each, whenSome } from '../ui/solid.ts';
import type { Child } from '../ui/solid.ts';
import type { Shell } from './shell.ts';

/** The account's pending changes, once read (none while they aren't). */
function pending(shell: Shell): readonly Extract<ChangeEntry, { readonly state: 'pending' }>[] {
  const changes = shell.manager.changes();
  return changes.kind === 'ok' ? changes.value.flatMap((change) => (change.state === 'pending' ? [change] : [])) : [];
}

/** Whether a session can act now (the banners' buttons need one). */
function live(shell: Shell): boolean {
  const status = shell.account.status();
  return status.kind === 'account' && !status.signedOut;
}

/** One banner (the library's strip Banner): its mark, its words and its buttons; a region is named `label`. */
function Notice(className: string, warn: boolean, role: 'alert' | 'region', label: string, title: () => string, text: () => string, ...buttons: readonly Child[]): Child {
  return component(Banner, {
    strip: true,
    tone: warn ? 'warn' : 'info',
    class: className,
    role,
    ...(role === 'region' ? { 'aria-label': label } : {}),
    get title() {
      return title();
    },
    children: text,
    actions: buttons,
  });
}

/** A banner's small button (the library's). */
function Small(label: string, onClick: () => void, className: string, variant: 'primary' | 'secondary' | 'ghost' = 'secondary', dialog = false): Child {
  return component(Button, { variant, size: 'sm', class: className, ...(dialog ? { 'aria-haspopup': 'dialog' } : {}), onClick, children: label });
}

/** The banners, in order of urgency. */
export function ManageBanners(shell: Shell): Child {
  const { t, ui, manager, account, backups } = shell;
  const label = t('account.banner.label');
  // Each region is named for what it is, so two at once are told apart (landmarks are unique).
  const cancel = (change: ChangeEntry, words: string, primary: boolean): Child =>
    Small(words, () => ui.setDialog({ kind: 'account', task: { kind: 'cancel', change } }), 'banner-cancel-change', primary ? 'primary' : 'secondary', true);
  return [
    // RQ4: a reset waiting, on every device.
    each(
      () => (live(shell) ? pending(shell).filter((change) => change.kind === 'reset') : []),
      (change) =>
        Notice(
          'reset-banner',
          true,
          'alert',
          label,
          () => t('account.banner.reset.title', { date: dateWords(shell, change.createdAt) }),
          () => t('account.banner.reset.text', { date: timeWords(shell, change.dueAt) }),
          cancel(change, t('account.banner.reset.stop'), true),
        ),
    ),
    // DA2: the account's deletion waiting.
    whenSome(
      () => (live(shell) ? (account.view()?.deletion ?? null) : null),
      (deletion) =>
        Notice(
          'deletion-banner',
          true,
          'alert',
          label,
          () => t('account.banner.deletion.title', { date: dateWords(shell, deletion().dueAt) }),
          () => t('account.banner.deletion.text'),
          Small(t('account.banner.deletion.cancel'), () => ui.setDialog({ kind: 'account', task: { kind: 'keep' } }), 'banner-keep', 'primary', true),
          Small(t('account.banner.deletion.export'), () => void backups.backUpNow(), 'banner-back-up'),
        ),
    ),
    // PD4: any other change waiting its time (a phished new device's queue, N9).
    each(
      () => (live(shell) ? pending(shell).filter((change) => change.kind !== 'reset') : []),
      (change) =>
        Notice(
          'change-banner',
          true,
          'region',
          t('account.banner.change.label', { change: changeWords(shell, change) }),
          () => t('account.banner.change.title', { change: changeWords(shell, change) }),
          () => t('account.banner.change.text', { date: timeWords(shell, change.dueAt) }),
          cancel(change, t('account.change.cancel'), false),
        ),
    ),
    // NO2: a new device's login, not seen here.
    whenSome(
      () => (live(shell) ? manager.unseen() : null),
      (notice) =>
        Notice(
          'new-device-banner',
          false,
          'region',
          t('account.banner.newDevice.label'),
          () => t('account.banner.newDevice.title', { date: timeWords(shell, notice().at) }),
          () => t('account.banner.newDevice.text'),
          Small(
            t('account.banner.newDevice.review'),
            () => {
              ui.setSettingsAt('settings-account');
              shell.go('settings');
            },
            'banner-review-devices',
          ),
          Small(t('account.banner.newDevice.seen'), () => void manager.seen(), 'banner-seen', 'ghost'),
        ),
    ),
    // TA1: the terms asked again; never blocking (C16).
    whenSome(
      () => (live(shell) && manager.terms()?.required === true ? LEGAL_VERSIONS : null),
      () => {
        const site = account.site();
        return Notice(
          'terms-banner',
          false,
          'region',
          t('account.banner.terms.label'),
          () => t('account.banner.terms.title'),
          () => t('account.banner.terms.text'),
          component(Button, { variant: 'link', href: `${site}/terms/`, target: '_blank', rel: 'noopener noreferrer', children: t('account.create.terms') }),
          component(Button, { variant: 'link', href: `${site}/privacy/`, target: '_blank', rel: 'noopener noreferrer', children: t('account.create.privacy') }),
          AcceptTerms(shell, 'banner-terms'),
        );
      },
    ),
  ];
}
